// ============================================================================
// WMS PM SCHEDULES: Materials, Labour and Equipment aggregated across the Work
// Method's activities (WMSDocument sections 4 to 6).
// ----------------------------------------------------------------------------
// Derived in code, never asked of the model, from the cost lines that are
// actually priced. Stage 3 only carries a partly priced picture: the model's
// own lines are unpriced until Stage 4 prices the client scope row they belong
// to. So the schedules are derived twice: once at generation from the
// activities, and again whenever a Work Method is bound to its final scope
// (bindWmsToScopeProjection), from the scope row's final lines.
//
// Rules this module keeps:
//   - A cost line is counted once. A scope row that groups several activities
//     contributes its lines once, attributed to the activity each line came
//     from. A line that cannot be attributed is attributed to the whole group.
//     A group total is never copied onto every activity.
//   - A validated quantity (a cost line's qty) and a planning quantity (the
//     model's materials_required qty) are different things. The planning qty
//     only shows as `planned_qty`, and only when the cost line has no
//     validated qty, so an unverified 5 L never reads as 5 L on order.
//   - Unknown stays unknown: a line with no price is counted in
//     `unpriced_lines` and the row says so, instead of adding a quiet $0.
// Pure: no I/O, no React.
// ============================================================================

import { pricingRowHasPendingResolution } from '../services/pricing-resolution';

type UnknownRecord = Record<string, unknown>;
type CostCategory = 'materials' | 'labour' | 'equipment' | 'other';
export type SchedulePricingStatus = 'priced' | 'partly_priced' | 'unpriced' | 'pending' | 'owned';
export type ScheduleQuantityStatus = 'validated' | 'planned_unverified' | 'partly_unverified' | 'unknown';

export interface MaterialScheduleRow {
    description: string;
    brand_or_spec: string | null;
    unit: string | null;
    total_qty: number;
    planned_qty?: number;
    quantity_status: ScheduleQuantityStatus;
    total_cost: number;
    pricing_status: SchedulePricingStatus;
    used_in_activities: string[];
    supplier_notes: string | null;
    grouped_lines?: number;
    unpriced_lines?: number;
    pending_lines?: number;
}

export interface LabourScheduleRow {
    trade: string;
    total_hours: number;
    total_workers: number;
    total_cost: number;
    avg_rate: number | null;
    pricing_status: SchedulePricingStatus;
    used_in_activities: string[];
    grouped_lines?: number;
    unpriced_lines?: number;
    pending_lines?: number;
}

export interface EquipmentScheduleRow {
    description: string;
    ownership: string;
    total_duration_days: number;
    total_cost: number;
    pricing_status: SchedulePricingStatus;
    used_in_activities: string[];
    grouped_lines?: number;
    unpriced_lines?: number;
    pending_lines?: number;
}

export interface WmsSchedules {
    materials: MaterialScheduleRow[];
    labour: LabourScheduleRow[];
    equipment: EquipmentScheduleRow[];
    totals: {
        materials: number;
        labour: number;
        equipment: number;
        other: number;
        direct: number;
        /** Lines (and planned-only requirements) with no stored price. */
        unpriced_lines: number;
        /** Lines whose stored amount is kept but whose pricing is still pending resolution. */
        pending_lines: number;
        /** True when every line is an established price, so `direct` is the whole direct cost. */
        complete: boolean;
    };
}

export interface DeriveSchedulesOptions {
    /** The client scope rows (Stage 4). Absent at generation time. */
    scope?: readonly unknown[] | null;
}

const COST_CATEGORIES: ReadonlyArray<{ category: CostCategory; keys: readonly string[] }> = [
    { category: 'materials', keys: ['materials'] },
    { category: 'labour', keys: ['labour'] },
    { category: 'equipment', keys: ['equipment'] },
    { category: 'other', keys: ['subcontractors', 'otherCosts', 'other'] },
];

function isRecord(value: unknown): value is UnknownRecord {
    return !!value && typeof value === 'object' && !Array.isArray(value);
}

function trim(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
}

function num(value: unknown): number {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    const parsed = parseFloat(value as string);
    return Number.isFinite(parsed) ? parsed : 0;
}

const round2 = (value: number): number => Math.round(value * 100) / 100;

function normaliseName(value: string): string {
    return value.toLowerCase().replace(/\s+/g, ' ').trim();
}

function identityOf(line: UnknownRecord): string {
    return normaliseName(
        trim(line.description) || trim(line.trade) || trim(line.role) || trim(line.item) || trim(line.name),
    );
}

function pricingIdOf(line: UnknownRecord): string {
    return trim(line.pricingItemId) || trim(line.pricing_item_id);
}

/** One cost line, counted once, with the activities it is attributed to. */
interface Contribution {
    category: CostCategory;
    line: UnknownRecord;
    activityIds: string[];
    grouped: boolean;
    /** The planning entry (materials_required / equipment_required) merged onto it. */
    planned?: UnknownRecord;
}

function activityIdOf(activity: UnknownRecord): string {
    return trim(activity.id) || trim(activity.title) || '?';
}

function linesOf(costBreakdown: unknown, category: CostCategory): UnknownRecord[] {
    if (!isRecord(costBreakdown)) return [];
    const spec = COST_CATEGORIES.find(entry => entry.category === category);
    return (spec?.keys || []).flatMap(key => (
        Array.isArray(costBreakdown[key]) ? (costBreakdown[key] as unknown[]).filter(isRecord) : []
    ));
}

function activityIdsOfScopeRow(row: UnknownRecord): string[] {
    const ids: string[] = [];
    const push = (value: unknown) => {
        if (typeof value === 'string' || typeof value === 'number') ids.push(String(value).trim());
        else if (isRecord(value)) ids.push(trim(value.activityId) || trim(value.activity_id) || trim(value.id));
    };
    if (isRecord(row.activityRef)) push(row.activityRef.activityId);
    for (const key of ['activityRefs', 'activity_refs', 'activity_ids', 'activityIds']) {
        if (Array.isArray(row[key])) (row[key] as unknown[]).forEach(push);
    }
    push(row.activity_id);
    return Array.from(new Set(ids.filter(Boolean)));
}

/**
 * Attribute a grouped row's final lines to the activities they came from. A
 * row's lines are its activities' lines in activity order, priced in place, so
 * each final line takes the first unclaimed line of the same identity. A line
 * with no such partner (renamed by pricing, or added by it) belongs to the
 * group as a whole and is not copied onto any one activity.
 */
function attributeGroupedLines(
    category: CostCategory,
    finalLines: UnknownRecord[],
    owners: ReadonlyArray<{ id: string; lines: UnknownRecord[] }>,
    groupIds: string[],
): Contribution[] {
    const candidates = owners.flatMap(owner => owner.lines.map(line => ({ owner: owner.id, line, used: false })));
    return finalLines.map((line) => {
        const finalId = pricingIdOf(line);
        const finalName = identityOf(line);
        const partner = candidates.find((candidate) => {
            if (candidate.used) return false;
            const ownId = pricingIdOf(candidate.line);
            if (finalId && ownId) return finalId === ownId;
            return !!finalName && finalName === identityOf(candidate.line);
        });
        if (!partner) return { category, line, activityIds: groupIds, grouped: true };
        partner.used = true;
        return { category, line, activityIds: [partner.owner], grouped: false };
    });
}

function collectContributions(
    activities: UnknownRecord[],
    scope: readonly unknown[] | null | undefined,
): Contribution[] {
    const byId = new Map<string, UnknownRecord>();
    for (const activity of activities) byId.set(activityIdOf(activity), activity);

    const contributions: Contribution[] = [];
    const coveredByRow = new Set<string>();
    for (const rawRow of Array.isArray(scope) ? scope : []) {
        if (!isRecord(rawRow) || !isRecord(rawRow.costBreakdown)) continue;
        const ids = activityIdsOfScopeRow(rawRow).filter(id => byId.has(id));
        if (ids.length === 0) continue;
        const rowLines = COST_CATEGORIES.map(({ category }) => ({
            category,
            lines: linesOf(rawRow.costBreakdown, category),
        }));
        // A row that holds a price but no cost lines says nothing about the
        // resources; the activities' own lines stay the record there.
        if (rowLines.every(entry => entry.lines.length === 0)) continue;
        ids.forEach(id => coveredByRow.add(id));
        for (const { category, lines } of rowLines) {
            if (ids.length === 1) {
                lines.forEach(line => contributions.push({ category, line, activityIds: ids, grouped: false }));
                continue;
            }
            const owners = ids.map(id => ({ id, lines: linesOf(byId.get(id)?.costBreakdown, category) }));
            contributions.push(...attributeGroupedLines(category, lines, owners, ids));
        }
    }
    for (const activity of activities) {
        const id = activityIdOf(activity);
        if (coveredByRow.has(id)) continue;
        for (const { category } of COST_CATEGORIES) {
            linesOf(activity.costBreakdown, category).forEach(line => (
                contributions.push({ category, line, activityIds: [id], grouped: false })
            ));
        }
    }
    return contributions;
}

/**
 * Merge each activity's planning list (materials_required, equipment_required)
 * onto the cost line it describes, or keep it as a planned-only requirement.
 * The cost line wins for quantity and cost. One planning entry counts once per
 * activity.
 */
function mergePlanningEntries(activities: UnknownRecord[], contributions: Contribution[]): Contribution[] {
    const planned: Contribution[] = [];
    for (const activity of activities) {
        const id = activityIdOf(activity);
        const lists: Array<{ category: 'materials' | 'equipment'; entries: unknown }> = [
            { category: 'materials', entries: activity.materials_required },
            { category: 'equipment', entries: activity.equipment_required },
        ];
        for (const { category, entries } of lists) {
            const seen = new Set<string>();
            for (const entry of Array.isArray(entries) ? entries : []) {
                if (!isRecord(entry)) continue;
                const description = trim(entry.description) || trim(entry.brand_or_spec) || trim(entry.notes);
                if (!description) continue;
                const key = normaliseName(description);
                if (seen.has(key)) continue;
                seen.add(key);
                const home = contributions.find(c => (
                    c.category === category
                    && !c.planned
                    && c.activityIds.includes(id)
                    && identityOf(c.line) === key
                ));
                if (home) home.planned = entry;
                else planned.push({ category, line: { description }, activityIds: [id], grouped: false, planned: entry });
            }
        }
    }
    return [...contributions, ...planned];
}

function pricingStatusOf(
    priced: number,
    unpriced: number,
    pending: number,
    ownedOnly: boolean,
): SchedulePricingStatus {
    if (ownedOnly) return 'owned';
    if (unpriced + pending === 0) return 'priced';
    // Nothing established: a kept amount awaiting resolution reads as pending,
    // no amount at all reads as unpriced.
    if (priced === 0) return pending > 0 ? 'pending' : 'unpriced';
    return 'partly_priced';
}

interface Facts {
    qty: number;
    plannedQty: number;
    total: number;
    /** An established amount: stated, positive, and not awaiting resolution. */
    priced: boolean;
    /** A stored amount whose pricing is still pending resolution and has no manual authority. */
    pending: boolean;
    owned: boolean;
}

/**
 * A builder's own manual cost or price is authority even on a row the catalogue
 * could not resolve (the same rule the Internal Price Working export applies),
 * so pending resolution does not downgrade it.
 */
function hasManualCommercialAuthority(line: UnknownRecord): boolean {
    const status = trim(line.pricingStatus ?? line.pricing_status).toLowerCase();
    const source = trim(line._rateSource ?? line._rate_source ?? line.rateSource ?? line.rate_source).toLowerCase();
    return line.manualOverride === true
        || ['manual_override', 'manual_cost_estimate'].includes(status)
        || ['project_manual', 'manual', 'manual_override', 'project_cost_estimate'].includes(source);
}

/** A total the line states: a finite number or a numeric string. Zero is a statement. */
function statedTotal(line: UnknownRecord): number | null {
    const raw = line.total;
    if (raw === null || raw === undefined || raw === '' || typeof raw === 'boolean') return null;
    const parsed = typeof raw === 'number' ? raw : parseFloat(String(raw).replace(/[$,\s]/g, ''));
    return Number.isFinite(parsed) ? parsed : null;
}

/**
 * What a line costs. A total the line states is the answer, including an
 * explicit zero: a line priced at $0 is not re-priced from a leftover rate, and
 * an unresolved line is not made to look priced. Only a line that states no
 * valid total falls back to its rate (labour by hours and crew, everything else
 * by quantity), and never while its pricing is still pending resolution.
 */
function lineTotal(line: UnknownRecord, category: CostCategory, qty: number): number {
    const stated = statedTotal(line);
    if (stated !== null) return stated;
    if (pricingRowHasPendingResolution(line)) return 0;
    return category === 'labour'
        ? num(line.rate) * num(line.hours) * (num(line.workers) || 1)
        : num(line.unitRate ?? line.unit_rate) * qty;
}

function factsOf(contribution: Contribution): Facts {
    const { line, planned, category } = contribution;
    const qty = Math.max(0, num(line.qty ?? line.quantity));
    const total = round2(lineTotal(line, category, qty));
    const plannedQty = planned ? Math.max(0, num(planned.qty)) : 0;
    const ownership = trim(line.ownership) || trim(planned?.ownership);
    // The stored amount is kept either way. Whether it is an ESTABLISHED price is
    // a separate fact: a stated total on a row still pending resolution (and with
    // no manual authority) is carried as pending, not as priced.
    const pending = total > 0 && pricingRowHasPendingResolution(line) && !hasManualCommercialAuthority(line);
    return {
        qty,
        // A planning quantity is only ever shown, and only when no cost line
        // carries a validated one.
        plannedQty: qty > 0 ? 0 : plannedQty,
        total,
        priced: total > 0 && !pending,
        pending,
        owned: category === 'equipment' && total <= 0 && ownership.toLowerCase() === 'owned',
    };
}

function quantityStatusOf(validated: number, plannedUnverified: number): ScheduleQuantityStatus {
    if (validated > 0 && plannedUnverified > 0) return 'partly_unverified';
    if (validated > 0) return 'validated';
    return plannedUnverified > 0 ? 'planned_unverified' : 'unknown';
}

function pushUnique(list: string[], ids: string[]): void {
    for (const id of ids) if (!list.includes(id)) list.push(id);
}

export function deriveSchedules(
    activities: unknown,
    options: DeriveSchedulesOptions = {},
): WmsSchedules {
    const list = (Array.isArray(activities) ? activities : []).filter(isRecord);
    const empty: WmsSchedules = {
        materials: [],
        labour: [],
        equipment: [],
        totals: { materials: 0, labour: 0, equipment: 0, other: 0, direct: 0, unpriced_lines: 0, pending_lines: 0, complete: true },
    };
    if (list.length === 0) return empty;

    const contributions = mergePlanningEntries(list, collectContributions(list, options.scope));

    interface Acc {
        description: string;
        brand: string | null;
        unit: string | null;
        notes: string | null;
        ownership: string;
        qty: number;
        plannedQty: number;
        hours: number;
        pricedHours: number;
        workers: number;
        days: number;
        total: number;
        pricedTotal: number;
        priced: number;
        unpriced: number;
        pending: number;
        owned: number;
        groupedLines: number;
        used: string[];
    }
    const rows: Record<'materials' | 'labour' | 'equipment', Map<string, Acc>> = {
        materials: new Map(),
        labour: new Map(),
        equipment: new Map(),
    };
    const totals = { materials: 0, labour: 0, equipment: 0, other: 0 };
    let unpricedLines = 0;
    let pendingLines = 0;

    for (const contribution of contributions) {
        const { category, line, planned } = contribution;
        const facts = factsOf(contribution);
        if (category === 'other') {
            totals.other += facts.total;
            if (facts.pending) pendingLines += 1;
            else if (!facts.priced) unpricedLines += 1;
            continue;
        }
        totals[category] += facts.total;
        if (facts.pending) pendingLines += 1;
        else if (!facts.priced && !facts.owned) unpricedLines += 1;

        const description = trim(line.description) || trim(planned?.brand_or_spec) || trim(planned?.notes);
        const name = category === 'labour' ? trim(line.trade) : description;
        if (!name) continue;
        const key = normaliseName(name);
        const acc = rows[category].get(key) || {
            description: name,
            brand: null,
            unit: null,
            notes: null,
            ownership: 'unknown',
            qty: 0,
            plannedQty: 0,
            hours: 0,
            pricedHours: 0,
            workers: 0,
            days: 0,
            total: 0,
            pricedTotal: 0,
            priced: 0,
            unpriced: 0,
            pending: 0,
            owned: 0,
            groupedLines: 0,
            used: [],
        };
        acc.brand = acc.brand || trim(planned?.brand_or_spec) || trim(line.brand_or_spec) || null;
        acc.unit = acc.unit || trim(line.unit) || trim(planned?.unit) || null;
        acc.notes = acc.notes || trim(planned?.notes) || null;
        if (acc.ownership === 'unknown') acc.ownership = trim(line.ownership) || trim(planned?.ownership) || 'unknown';
        acc.qty += facts.qty;
        acc.plannedQty += facts.plannedQty;
        const hours = num(line.hours);
        acc.hours += hours;
        if (facts.priced) {
            acc.pricedHours += hours;
            acc.pricedTotal += facts.total;
        }
        acc.workers = Math.max(acc.workers, num(line.workers) || 1);
        acc.days = Math.max(acc.days, num(line.duration_days) || num(planned?.duration_days));
        acc.total += facts.total;
        if (facts.priced) acc.priced += 1;
        else if (facts.pending) acc.pending += 1;
        else if (facts.owned) acc.owned += 1;
        else acc.unpriced += 1;
        if (contribution.grouped) acc.groupedLines += 1;
        pushUnique(acc.used, contribution.activityIds);
        rows[category].set(key, acc);
    }

    const byCost = <T extends { total_cost: number }>(a: T, b: T) => b.total_cost - a.total_cost;
    const status = (acc: Acc) => pricingStatusOf(
        acc.priced,
        acc.unpriced,
        acc.pending,
        acc.priced === 0 && acc.unpriced === 0 && acc.pending === 0 && acc.owned > 0,
    );
    const grouped = (acc: Acc) => (acc.groupedLines > 0 ? { grouped_lines: acc.groupedLines } : {});
    // What stops a row being fully established, so a display can say so without
    // adding a pending amount (already inside total_cost) to anything.
    const gaps = (acc: Acc) => ({
        ...(acc.unpriced > 0 ? { unpriced_lines: acc.unpriced } : {}),
        ...(acc.pending > 0 ? { pending_lines: acc.pending } : {}),
    });

    const materials: MaterialScheduleRow[] = [...rows.materials.values()].map(acc => ({
        description: acc.description,
        brand_or_spec: acc.brand,
        unit: acc.unit,
        total_qty: round2(acc.qty),
        ...(acc.plannedQty > 0 ? { planned_qty: round2(acc.plannedQty) } : {}),
        quantity_status: quantityStatusOf(acc.qty, acc.plannedQty),
        total_cost: round2(acc.total),
        pricing_status: status(acc),
        used_in_activities: acc.used,
        supplier_notes: acc.notes,
        ...grouped(acc),
        ...gaps(acc),
    })).sort(byCost);
    const labour: LabourScheduleRow[] = [...rows.labour.values()].map(acc => ({
        trade: acc.description,
        total_hours: acc.hours,
        total_workers: acc.workers,
        total_cost: round2(acc.total),
        // A day rate over its eight hours, from established prices only: hours
        // with no price (or a price still pending resolution) are left out so an
        // unpriced sibling cannot drag the rate down.
        avg_rate: acc.pricedHours > 0 ? round2(acc.pricedTotal / acc.pricedHours) : null,
        pricing_status: status(acc),
        used_in_activities: acc.used,
        ...grouped(acc),
        ...gaps(acc),
    })).sort(byCost);
    const equipment: EquipmentScheduleRow[] = [...rows.equipment.values()].map(acc => ({
        description: acc.description,
        ownership: acc.ownership,
        total_duration_days: acc.days,
        total_cost: round2(acc.total),
        pricing_status: status(acc),
        used_in_activities: acc.used,
        ...grouped(acc),
        ...gaps(acc),
    })).sort(byCost);

    const direct = totals.materials + totals.labour + totals.equipment + totals.other;
    return {
        materials,
        labour,
        equipment,
        totals: {
            materials: round2(totals.materials),
            labour: round2(totals.labour),
            equipment: round2(totals.equipment),
            other: round2(totals.other),
            direct: round2(direct),
            unpriced_lines: unpricedLines,
            pending_lines: pendingLines,
            complete: unpricedLines === 0 && pendingLines === 0,
        },
    };
}

export interface PlannedAmount {
    quantity: number;
    unit: string | null;
}

export interface PlannedQuantityHint {
    /**
     * One amount per distinct unit, in the order first planned. Amounts in the SAME unit are added; amounts in
     * different units are never added or converted ("5 L" and "2 x 20 L pail" are two amounts, not 7 L).
     */
    amounts: PlannedAmount[];
}

/**
 * The model's PLANNING quantities (materials_required / equipment_required `qty`) for the activities one client
 * scope row references, keyed `<category>:<normalised name>`. This is a read-only hint for a screen to show beside a
 * line that has no validated quantity. It is never a quantity to price or order, and nothing here writes it to a
 * cost line. A row whose activity references resolve to nothing has no hint: the hint is never guessed from the
 * whole Work Method. One planning entry counts once per activity, as in the schedules. Units are compared as written
 * (case and spacing aside): no conversion between units or packages is established, so none is assumed.
 */
export function plannedQuantityHints(
    activities: unknown,
    scopeRow: unknown,
): Map<string, PlannedQuantityHint> {
    const hints = new Map<string, PlannedQuantityHint>();
    if (!isRecord(scopeRow)) return hints;
    const byId = new Map<string, UnknownRecord>();
    for (const activity of (Array.isArray(activities) ? activities : []).filter(isRecord)) {
        byId.set(activityIdOf(activity), activity);
    }
    for (const id of activityIdsOfScopeRow(scopeRow)) {
        const activity = byId.get(id);
        if (!activity) continue;
        const lists: Array<{ category: 'materials' | 'equipment'; entries: unknown }> = [
            { category: 'materials', entries: activity.materials_required },
            { category: 'equipment', entries: activity.equipment_required },
        ];
        for (const { category, entries } of lists) {
            const seen = new Set<string>();
            for (const entry of Array.isArray(entries) ? entries : []) {
                if (!isRecord(entry)) continue;
                const description = trim(entry.description) || trim(entry.brand_or_spec) || trim(entry.notes);
                const quantity = Math.max(0, num(entry.qty));
                if (!description || !(quantity > 0)) continue;
                const name = normaliseName(description);
                if (seen.has(name)) continue;
                seen.add(name);
                const key = `${category}:${name}`;
                const unit = trim(entry.unit) || null;
                const unitKey = (unit ?? '').toLowerCase().replace(/\s+/g, ' ');
                const amounts = hints.get(key)?.amounts ?? [];
                const sameUnit = amounts.find(amount => (amount.unit ?? '').toLowerCase().replace(/\s+/g, ' ') === unitKey);
                if (sameUnit) sameUnit.quantity = round2(sameUnit.quantity + quantity);
                else amounts.push({ quantity: round2(quantity), unit });
                hints.set(key, { amounts });
            }
        }
    }
    return hints;
}

/** The key plannedQuantityHints uses for a cost line. */
export function plannedQuantityHintKey(category: string, label: string): string {
    const base = category === 'equipment' ? 'equipment' : 'materials';
    return `${base}:${normaliseName(String(label || ''))}`;
}
